import {
	getMipOptionsForSelect,
	getNewPersonsOptionsForSelect,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import { getEntityName } from '@dealstack/frontend/shared/cap';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	getPersonById,
	getPersonsForCap,
	getPersonTagsForDeal,
} from '@dealstack/frontend/app/services/Person.service.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { useMipsByDealId } from '@dealstack/frontend/app/pages/Project/MIP/hooks';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import {
	DEAL_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { PersonType } from '@dealstack/shared/lib/generated';

export const useCapManageController = () => {
	const navigate = useNavigate();
	const urlParams = useParams();

	const [createEntityMode, setCreateEntityMode] = useState<PersonType | null>(null);
	const [newEntityComputedName, setNewEntityComputedName] = useState<string | null>(null);

	const { dealId, deal, isAllTransactionValidInDeal, refreshCapDetails } =
		useContext(DealContext);

	const { value: capForceDeleteFlag, loading: isForceDeleteFlagLoading } =
		useDealstackFeatureFlag(FEATURE_FLAG.ENABLE_CAP_FORCE_DELETE);

	useEffect(() => {
		setNewEntityComputedName(null);
	}, [createEntityMode]);

	const setSelectedEntityId = useCallback(
		(entityId: string | null, subRoute?: string) => {
			if (!entityId) {
				const path = generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}`, {
					dealId,
					personId: '',
				});
				navigate(path, { replace: true });
			} else {
				const currentSubRoute = urlParams['*'] ?? '';
				const tabParam = subRoute || currentSubRoute || PEOPLE_ROUTES.PERSON_DETAILS;

				const formattedSubRoute = !tabParam.startsWith('/') ? tabParam : tabParam.slice(1);

				const path = generatePath(
					`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}/${formattedSubRoute}`,
					{
						dealId,
						personId: entityId,
					}
				);

				navigate(path, { replace: true });
			}
		},
		[navigate, urlParams, dealId]
	);

	const selectedEntityId = urlParams.entityId === ':personId' ? null : urlParams.personId;

	const getSelectedEntityRequest = useQuery({
		queryKey: [API_PATHS.PERSON_PERSONID_GET, { personId: selectedEntityId ?? '' }],
		queryFn: () =>
			getPersonById(API_PATHS.PERSON_PERSONID_GET, { personId: selectedEntityId ?? '' }),
		enabled: !!selectedEntityId,
		placeholderData: keepPreviousData,
	});

	const selectedEntityName = useMemo(() => {
		if (selectedEntityId) {
			return getEntityName(getSelectedEntityRequest.data);
		}

		return '';
	}, [getSelectedEntityRequest.data, selectedEntityId]);

	const selectedEntityRegNumber = useMemo(() => {
		return getSelectedEntityRequest.data?.registrationDetails?.corporateRegistrationNumber;
	}, [getSelectedEntityRequest.data]);

	const getPersonsRequest = useQuery({
		queryKey: [API_PATHS.PERSON_DEAL_DEALID_PERSONS_FOR_CAP_GET, { dealId }],
		queryFn: () =>
			getPersonsForCap(API_PATHS.PERSON_DEAL_DEALID_PERSONS_FOR_CAP_GET, { dealId }),
		enabled: !!dealId,
	});

	const getPersonsTagsRequest = useQuery({
		queryKey: [API_PATHS.PERSON_DEAL_DEALID_PERSON_TAGS_GET, { dealId }],
		queryFn: () =>
			getPersonTagsForDeal(API_PATHS.PERSON_DEAL_DEALID_PERSON_TAGS_GET, { dealId }),
		enabled: !!dealId,
	});

	const personsSelectOptions = useMemo(
		() => getNewPersonsOptionsForSelect(getPersonsRequest.data),
		[getPersonsRequest.data]
	);

	const getMipsRequest = useMipsByDealId(dealId);

	const mipSelectOptions = useMemo(() => {
		const x = getMipOptionsForSelect(getMipsRequest.data);

		return x;
	}, [getMipsRequest.data]);

	const handleEntityDeletionNavigation = useCallback(
		async (deletedEntityIds: string[]) => {
			const { data: updatedPersons } = await getPersonsRequest.refetch();

			const wasCurrentEntityDeleted =
				selectedEntityId && deletedEntityIds.includes(selectedEntityId);

			if (!updatedPersons?.length) {
				setSelectedEntityId(null);
			} else if (wasCurrentEntityDeleted || !selectedEntityId) {
				const firstEntity = updatedPersons[0];
				setSelectedEntityId(firstEntity.id, PEOPLE_ROUTES.PERSON_DETAILS);
			}
		},
		[getPersonsRequest, selectedEntityId, setSelectedEntityId]
	);

	const refetchPersonsAndTags = async () => {
		return await Promise.all([getPersonsRequest.refetch(), getPersonsTagsRequest.refetch()]);
	};

	return {
		persons: getPersonsRequest.data,
		refetchPersons: () => getPersonsRequest.refetch(),
		isFetchingPersons: getPersonsRequest.isFetching,
		isInitialLoading: getPersonsRequest.isInitialLoading,
		personsTags: getPersonsTagsRequest.data,
		isFetchingPersonsTags: getPersonsTagsRequest.isFetching,
		refetchPersonsAndTags,
		selectedEntityId: selectedEntityId ?? '',
		isFetchingSelectedEntityData: getSelectedEntityRequest.isFetching,
		selectedEntityData: getSelectedEntityRequest.data,
		refetchSelectedEntity: getSelectedEntityRequest.refetch,
		setSelectedEntityId,
		selectedEntityName,
		selectedEntityRegNumber,
		mipSelectOptions,
		personsSelectOptions,
		dealId,
		isAllTransactionValidInDeal,
		dealName: deal?.name,
		isForceDeleteFlagLoading,
		capForceDeleteFlag,
		dealCurrency: deal?.currency,
		handleEntityDeletionNavigation,
		createEntityMode,
		setCreateEntityMode,
		newEntityComputedName,
		setNewEntityComputedName,
		refreshCapDetails,
	};
};
